import type { MicrotrendResearchLegObservation, MicrotrendRustSnapshot } from '@einhaus/project314-types';
import { getDateFromUnix } from '@einhaus/meteoric-helpers';

const number = (value: number | null, digits = 2) => (value === null ? 'Unavailable' : value.toFixed(digits));
const percent = (value: number | null) => (value === null ? 'Unavailable' : `${number(value)}%`);
const time = (at: number) => getDateFromUnix(at, 'ymdhms');
const criterionLabels: Record<string, string> = {
    displacementAtr: 'move size',
    speedAtrPerBar: 'speed',
    efficiency: 'directness',
    internalPullback: 'internal pullbacks',
    barIntervals: 'move length',
    positiveOriginAtr: 'usable volatility history',
    interruptedBeforeConfirmation: 'ended without confirmation'
};

export function microtrendQualificationLabel(leg: MicrotrendResearchLegObservation): string {
    switch (leg.qualification) {
        case 'candidate':
            return 'Potential impulse';
        case 'confirmedImpulse':
            return 'Confirmed impulse · research rules';
        case 'criteriaNotMet':
            return leg.lifecycle === 'developing' ? 'Impulse criteria not yet met' : 'Impulse criteria not met';
        case 'unavailable':
            return 'Impulse assessment unavailable';
        case 'interrupted':
            return 'Interrupted · unconfirmed';
    }
}

export default function MicrotrendObservationPanel({
    rust,
    selectedLegId,
    onReplay
}: {
    rust: MicrotrendRustSnapshot;
    selectedLegId: string | undefined;
    onReplay: (cutoff: number) => void;
}) {
    const frame = rust.observations.at(-1);
    const current = frame?.current;
    const policy = rust.observationPolicy;
    const history = rust.observations.flatMap((observation) =>
        [observation.confirmed, observation.interrupted, observation.current]
            .filter((leg): leg is MicrotrendResearchLegObservation => leg !== null && (!selectedLegId || leg.legId === selectedLegId))
            .map((leg) => ({ observation, leg }))
    );
    const visible = history.slice(-100).reverse();
    return (
        <section aria-label="What Rust knew at this point" className="my-3 rounded border border-sky-900 bg-slate-950 p-3 text-sm">
            <h3 className="font-semibold text-sky-200">What Rust knew at this point</h3>
            <p className="mt-1 text-xs text-zinc-400">
                Completed candles only. These observations are preserved as they were known then. Potential impulses can change or
                disappear; they are not confirmed moves or entry signals.
            </p>
            {current ? (
                <>
                    <p className="mt-3" data-testid="microtrend-current-observation">
                        <strong>{current.direction === 'up' ? 'Upward' : 'Downward'} move · developing</strong>
                        {' · '}
                        {current.behavior === 'extending'
                            ? 'Setting a new extreme'
                            : current.behavior === 'retracing'
                              ? 'Pulling back from its extreme'
                              : 'Holding its extreme'}
                        {' · '}
                        {microtrendQualificationLabel(current)}
                    </p>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                        <dt className="text-zinc-400">Move so far</dt>
                        <dd>{number(current.priceChangePct)}%</dd>
                        <dt className="text-zinc-400">Move / recent volatility</dt>
                        <dd>{current.displacementAtr === null ? 'Unavailable' : `${number(current.displacementAtr)} ATR`}</dd>
                        <dt className="text-zinc-400">Speed / bar</dt>
                        <dd>{current.speedAtrPerBar === null ? 'Unavailable' : `${number(current.speedAtrPerBar)} ATR`}</dd>
                        <dt className="text-zinc-400">Directness</dt>
                        <dd>{number(current.efficiency * 100)}%</dd>
                        <dt className="text-zinc-400">Retracement from extreme</dt>
                        <dd>{percent(current.currentRetracementFraction === null ? null : current.currentRetracementFraction * 100)}</dd>
                        <dt className="text-zinc-400">Size rank in prior moves</dt>
                        <dd>
                            {current.history.sizePercentile === null
                                ? 'Insufficient history'
                                : `${number(current.history.sizePercentile, 0)} / 100`}
                        </dd>
                    </dl>
                    {current.criteria.length > 0 && (
                        <p className="mt-2 text-xs text-amber-200">
                            Missing criteria: {current.criteria.map((c) => criterionLabels[c] ?? c).join(', ')}.
                        </p>
                    )}
                    <p className="mt-2 text-xs text-zinc-400">
                        Reference: {current.history.sampleCount} earlier confirmed {current.history.sampleCount === 1 ? 'move' : 'moves'} in
                        this loaded history; at least {policy.minimumHistorySamples} required. Frozen before{' '}
                        {time(current.history.frozenBefore.availableAt)} ET, when this leg was first detected. Median size{' '}
                        {percent(current.history.medianAbsoluteReturnPct)}; 90th-percentile size{' '}
                        {percent(current.history.p90AbsoluteReturnPct)}.
                    </p>
                </>
            ) : (
                <p className="mt-3" data-testid="microtrend-current-observation">
                    {frame?.state === 'warmingUp'
                        ? 'Warming up · more completed candles are needed.'
                        : 'No developing directional move at this cutoff.'}
                    {frame?.interrupted ? ' The latest move ended without reversal confirmation.' : ''}
                </p>
            )}
            {frame?.confirmed && (
                <p className="mt-2 text-xs text-sky-200">
                    Newly confirmed: {frame.confirmed.direction} move · {microtrendQualificationLabel(frame.confirmed)}.
                </p>
            )}
            <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-zinc-300">Research rules and limits</summary>
                <p className="mt-2 text-zinc-400">
                    {rust.observationVersion}: move ≥ {policy.impulse.minimumDisplacementAtr} ATR; speed ≥{' '}
                    {policy.impulse.minimumSpeedAtrPerBar} ATR/bar; directness ≥ {policy.impulse.minimumEfficiency * 100}%; internal
                    pullback ≤ {policy.impulse.maximumPullbackFraction * 100}% of displacement; at least{' '}
                    {policy.impulse.minimumBarIntervals} bar intervals. These fixed research settings are not tuned or validated trading
                    criteria.
                </p>
                <p className="mt-2 text-zinc-400">
                    Size ranks compare displacement so far with final sizes of earlier confirmed moves, using at most{' '}
                    {policy.maximumHistorySamples} moves from {policy.maximumHistoryAgeSeconds / 86400} days. They are not forecasts or a
                    fully calibrated symbol baseline. Interrupted moves are excluded. Current retracement refers to this leg's wick extreme;
                    it does not identify a parent trend. A new extreme can occur even when the candle closes below its high or above its
                    low.
                </p>
            </details>
            <details className="mt-3">
                <summary className="cursor-pointer text-sky-200">
                    {selectedLegId ? 'Selected move: observation history' : 'Observation history'} · {history.length} records
                </summary>
                <p className="my-2 text-xs text-zinc-400">
                    Latest {visible.length} records, newest first. Replay a bar to inspect an earlier cutoff. A bar and a session
                    interruption can share a timestamp; replay includes both in their original order.
                </p>
                <div className="max-h-64 overflow-auto">
                    <table className="w-full text-left text-xs">
                        <caption className="sr-only">Historical interpretations available at each observation</caption>
                        <thead>
                            <tr>
                                <th className="p-2">Known at (ET)</th>
                                <th className="p-2">Move / state</th>
                                <th className="p-2">Assessment then</th>
                                <th className="p-2">Move %</th>
                                <th className="p-2">Size rank</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(({ observation, leg }) => (
                                <tr key={`${observation.inputIndex}:${leg.legId}:${leg.lifecycle}`}>
                                    <td className="p-2">
                                        <button
                                            type="button"
                                            className="text-sky-300 underline"
                                            onClick={() => onReplay(observation.observedThrough.sequence + 1)}
                                        >
                                            {time(observation.observedThrough.availableAt)}
                                        </button>
                                    </td>
                                    <td className="p-2">
                                        {leg.direction} · {leg.lifecycle}
                                        {leg.behavior ? ` · ${leg.behavior}` : ''}
                                    </td>
                                    <td className="p-2">{microtrendQualificationLabel(leg)}</td>
                                    <td className="p-2">{number(leg.priceChangePct)}</td>
                                    <td className="p-2">
                                        {leg.history.sizePercentile === null ? 'Unavailable' : number(leg.history.sizePercentile, 0)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </section>
    );
}
